import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, Play, Pause, X, Plus,
  CheckCircle, AlertTriangle, Settings
} from 'lucide-react';

const ScheduledPublishingPanel = ({ walkId, walkTitle }) => {
  const [schedule, setSchedule] = useState(null);
  const [allSchedules, setAllSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    schedule_type: 'scheduled',
    publish_at: '',
    unpublish_at: '',
    timezone: 'UTC'
  });

  useEffect(() => {
    if (walkId) {
      fetchWalkSchedule();
    }
    fetchAllSchedules();
  }, [walkId]);

  const fetchWalkSchedule = async () => {
    try {
      const token = localStorage.getItem('cms_token') || 'editor-user-token';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/walks/${walkId}/schedule`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSchedule(data);
      } else if (response.status === 404) {
        setSchedule(null);
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
  };

  const fetchAllSchedules = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cms_token') || 'editor-user-token';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/schedules?status=pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllSchedules(data);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSchedule = async () => {
    try {
      const token = localStorage.getItem('cms_token') || 'editor-user-token';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/walks/${walkId}/schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scheduleForm)
      });

      if (response.ok) {
        const data = await response.json();
        setSchedule(data);
        setShowScheduleModal(false);
        fetchAllSchedules();
        
        // Reset form
        setScheduleForm({
          schedule_type: 'scheduled',
          publish_at: '',
          unpublish_at: '',
          timezone: 'UTC'
        });
      }
    } catch (error) {
      console.error('Error creating schedule:', error);
    }
  };

  const cancelSchedule = async () => {
    try {
      const token = localStorage.getItem('cms_token') || 'editor-user-token';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/walks/${walkId}/schedule`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSchedule(null);
        fetchAllSchedules();
      }
    } catch (error) {
      console.error('Error cancelling schedule:', error);
    }
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'executed': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const ScheduleModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Schedule Publishing</h3>
            <button
              onClick={() => setShowScheduleModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Walk: <span className="font-medium">{walkTitle}</span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Type
              </label>
              <select
                value={scheduleForm.schedule_type}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, schedule_type: e.target.value }))}
                className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="immediate">Publish Immediately</option>
                <option value="scheduled">Schedule for Later</option>
              </select>
            </div>

            {scheduleForm.schedule_type === 'scheduled' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduleForm.publish_at}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, publish_at: e.target.value }))}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unpublish Date & Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduleForm.unpublish_at}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, unpublish_at: e.target.value }))}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={scheduleForm.timezone}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, timezone: e.target.value }))}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowScheduleModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={createSchedule}
              disabled={scheduleForm.schedule_type === 'scheduled' && !scheduleForm.publish_at}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Calendar className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Publishing Schedule</h3>
          </div>
          {walkId && (
            <button
              onClick={() => setShowScheduleModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {walkId ? (
          <div className="space-y-6">
            {/* Current Walk Schedule */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Current Walk Schedule</h4>
              {schedule ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center mb-2">
                        <Clock className="w-4 h-4 text-blue-600 mr-2" />
                        <span className="text-sm font-medium text-blue-900">
                          {schedule.schedule_type === 'immediate' ? 'Immediate Publishing' : 'Scheduled Publishing'}
                        </span>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(schedule.status)}`}>
                          {schedule.status}
                        </span>
                      </div>
                      {schedule.publish_at && (
                        <p className="text-sm text-blue-800">
                          Publish: {formatDateTime(schedule.publish_at)}
                        </p>
                      )}
                      {schedule.unpublish_at && (
                        <p className="text-sm text-blue-800">
                          Unpublish: {formatDateTime(schedule.unpublish_at)}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={cancelSchedule}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
                  <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No schedule set</p>
                  <button
                    onClick={() => setShowScheduleModal(true)}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Schedule publishing
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600">Select a walk to manage its publishing schedule</p>
          </div>
        )}

        {/* All Schedules Overview */}
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Upcoming Schedules</h4>
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : allSchedules.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <Clock className="w-6 h-6 mx-auto mb-2 text-gray-400" />
              <p>No upcoming scheduled publications</p>
            </div>
          ) : (
            <div className="space-y-2">
              {allSchedules.slice(0, 5).map((scheduleItem) => (
                <div key={scheduleItem.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 text-gray-500 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Walk ID: {scheduleItem.walk_id}</p>
                      <p className="text-xs text-gray-500">
                        {formatDateTime(scheduleItem.publish_at)}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(scheduleItem.status)}`}>
                    {scheduleItem.status}
                  </span>
                </div>
              ))}
              {allSchedules.length > 5 && (
                <p className="text-center text-sm text-gray-500 pt-2">
                  and {allSchedules.length - 5} more...
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && <ScheduleModal />}
    </div>
  );
};

export default ScheduledPublishingPanel;